// frontend/src/pages/UpgradePlan.jsx
import React, { useEffect, useState } from "react";
import { ArrowLeft, LogOut } from "lucide-react";
import { getAllPlanForUnAuth } from "../api/apiFunction/planServices";
import { createSubscriptionCheckout } from "../api/apiFunction/paymentServices";
import { getReferralById } from "../api/apiFunction/referralServices";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { LogoWithText } from "../components/Logo";
import ThemeOverrideHandler from "../components/ThemeOverrideHandler";
import AuthShowcase from "../components/pages/auth/AuthShowcase";

const UpgradePlan = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [loadingDiscount, setLoadingDiscount] = useState(false);
  const [referralId, setReferralId] = useState(null);
  const [user, setUser] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    // Load logged-in user from localStorage for display + logout
    const userData = localStorage.getItem("user");
    if (userData) {
      try {
        setUser(JSON.parse(userData));
      } catch (error) {}
    }

    const fetchPlans = async () => {
      try {
        const res = await getAllPlanForUnAuth();
        if (res?.data) {
          setPlans(res.data);
        }
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };
    fetchPlans();
  }, []);

  // Fetch referral discount data
  useEffect(() => {
    const fetchReferralDiscount = async () => {
      const userObj = localStorage.getItem("user")
        ? JSON.parse(localStorage.getItem("user"))
        : null;
      const userId = userObj?._id || userObj?.id || null;

      if (userId) {
        try {
          const res = await getReferralById(userId);
          if (res?.data && res.data.length > 0) {
            const discount = res.data[0]?.discount_amount || 0;
            setDiscountAmount(discount);
            // Store referralId for checkout
            setReferralId(res.data[0]?._id || null);
          }
        } catch (error) {
          setDiscountAmount(0);
        } finally {
          setLoadingDiscount(false);
        }
      } else {
        setLoadingDiscount(false);
      }
    };
    fetchReferralDiscount();
  }, []);

  // Get user type from localStorage and filter plans accordingly
  const userObj = localStorage.getItem("user")
    ? JSON.parse(localStorage.getItem("user"))
    : null;
  const userRole = userObj?.role || "individual";

  // Show organization plan for employees, individual plan for others
  const planType = userRole === "employee" ? "organization" : "individual";
  const filteredPlans = plans.filter((plan) => plan.planType === planType);
  const plan = filteredPlans[0];

  // Calculate discounted price if discount exists
  const originalPrice = plan?.baseCost || 0;
  const discountedPrice = Math.max(0, originalPrice - discountAmount);
  const hasDiscount = discountAmount > 0;

  // Helper function to format limit value
  const formatLimit = (value) => {
    if (value === -1 || value === "Unlimited") return "Unlimited";
    return typeof value === "number" ? value.toLocaleString() : value || "N/A";
  };

  // Manually format plan features for display
  const features = plan
    ? [
        {
          label: "High-end tokens",
          daily: formatLimit(plan.dailyLimits?.highEndLlmTokens),
          monthly: formatLimit(plan.monthlyLimits?.highEndLlmTokens),
        },
        {
          label: "Low-end tokens",
          daily: formatLimit(plan.dailyLimits?.smallLlmTokens),
          monthly: formatLimit(plan.monthlyLimits?.smallLlmTokens),
        },
        {
          label: "Image generations",
          daily: formatLimit(plan.dailyLimits?.images),
          monthly: formatLimit(plan.monthlyLimits?.images),
        },
        {
          label: "Web searches",
          daily: formatLimit(plan.dailyLimits?.webSearches),
          monthly: formatLimit(plan.monthlyLimits?.webSearches),
        },
      ]
    : [];

  // Toast notifications
  const showToastError = (message) => toast.error(message);

  // Handle Stripe subscription checkout
  const handleSubscribe = async () => {
    const localUser = localStorage.getItem("user")
      ? JSON.parse(localStorage.getItem("user"))
      : null;
    const userId = localUser?._id || localUser?.id || null;

    if (!userId) {
      showToastError("Please sign in to subscribe.");
      navigate("/login");
      return;
    }

    if (!plan) {
      showToastError("Plan not found.");
      return;
    }

    try {
      setIsPageLoading(true);

      // Use Stripe provider for subscription checkout
      const res = await createSubscriptionCheckout(
        plan._id,
        userId,
        referralId,
        "stripe", // Stripe payment provider
        "monthly"
      );

      if (res.data && res.data.checkoutUrl) {
        // Store session ID for verification later
        if (res.data.sessionId || res.data.chargeId) {
          localStorage.setItem(
            "payment_session_id",
            res.data.sessionId || res.data.chargeId
          );
        }
        // Redirect to Stripe checkout
        window.location.href = res.data.checkoutUrl;
      } else {
        showToastError("Failed to initiate payment.");
        setIsPageLoading(false);
      }
    } catch (err) {
      showToastError(
        err?.response?.data?.message || "Payment initiation failed."
      );
      setIsPageLoading(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleLogout = () => {
    localStorage.removeItem("user");
    setUser(null);
    navigate("/login", { replace: true });
  };

  const getInitial = (name) => {
    return name ? name.charAt(0).toUpperCase() : "U";
  };

  return (
    <>
      <ThemeOverrideHandler theme="light" />
      <div className="h-screen flex flex-col lg:flex-row bg-bg-50 overflow-hidden">
        {/* Left Section - Plan Details & Subscribe */}
        <div className="w-full lg:w-1/2 bg-bg-70 flex flex-col p-6 md:p-12 overflow-y-auto h-full">
          <div className="max-w-md mx-auto w-full">
            {/* Header with back arrow, logo, and logged-in user controls */}
            <div className="flex items-center justify-between gap-3 mb-16">
              <div className="flex items-center gap-3">
                <button
                  onClick={handleBack}
                  className="p-2 hover:bg-bg-60 rounded-lg transition-colors"
                  aria-label="Go back"
                >
                  <ArrowLeft className="w-5 h-5 text-fg-60" />
                </button>
                <LogoWithText
                  className="h-7 w-auto max-w-32"
                  fillClassName="fill-fg-40 hover:fill-fg-30 transition-colors"
                />
              </div>

              {/* Logged-in user quick controls (avatar + logout) */}
              {user && (
                <div className="hidden md:flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-ac-02 flex items-center justify-center border border-bd-50">
                    <span className="text-white dark:text-black text-sm font-semibold">
                      {getInitial(user.name)}
                    </span>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="p-1.5 rounded-full text-fg-60 hover:bg-bg-60 transition-colors border border-bd-50 bg-bg-70"
                    title="Logout"
                    aria-label="Logout"
                  >
                    <LogOut size={16} />
                  </button>
                </div>
              )}
            </div>

            {/* Plan Content */}
            {loading || loadingDiscount ? (
              <div className="space-y-6 animate-pulse">
                {/* Badge skeleton */}
                <div className="h-6 w-24 bg-bg-60 rounded-full"></div>

                {/* Title skeleton */}
                <div className="h-8 w-48 bg-bg-60 rounded"></div>

                {/* Price skeleton */}
                <div className="h-10 w-40 bg-bg-60 rounded"></div>

                {/* Features table skeleton */}
                <div className="pt-4">
                  <div className="h-4 w-32 bg-bg-60 rounded mb-6"></div>
                  <div className="space-y-3">
                    {[...Array(4)].map((_, i) => (
                      <div key={i} className="grid grid-cols-3 gap-4">
                        <div className="h-4 w-32 bg-bg-60 rounded"></div>
                        <div className="h-4 w-16 bg-bg-60 rounded"></div>
                        <div className="h-4 w-20 bg-bg-60 rounded"></div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Button skeleton */}
                <div className="h-10 w-full bg-bg-60 rounded mt-8"></div>
              </div>
            ) : plan ? (
              <>
                {/* User details card */}
                {user && (
                  <div className="mb-8 p-4 rounded-2xl bg-bg-60 border border-bd-50 shadow-sm">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-ac-02 flex items-center justify-center border border-bd-50">
                          <span className="text-white dark:text-black text-sm font-semibold">
                            {getInitial(user.name)}
                          </span>
                        </div>
                        <div className="flex flex-col leading-tight">
                          <span className="text-xs font-medium text-fg-40">
                            {user.name || "User"}
                          </span>
                          <span className="text-[11px] text-fg-60 truncate max-w-[220px]">
                            {user.email}
                          </span>
                        </div>
                      </div>
                      <span className="px-2 py-0.5 rounded-full bg-bg-50 text-[11px] text-fg-60 border border-bd-50">
                        {user.role || "individual"}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-3 text-[11px] text-fg-60">
                      <div>
                        <span className="block text-[10px] uppercase tracking-wide text-fg-70">
                          Plan
                        </span>
                        <span className="font-medium text-fg-40">
                          {user.plan?.name || plan?.name || "Pro"}
                        </span>
                      </div>
                      <div>
                        <span className="block text-[10px] uppercase tracking-wide text-fg-70">
                          Trial status
                        </span>
                        <span className="font-medium text-fg-40 capitalize">
                          {user.trial?.status || "expired"}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Plan Type Badge */}
                <div className="mb-6">
                  <span className="inline-block px-3 py-1 bg-bg-60 text-fg-60 text-xs font-medium rounded-full">
                    {planType.charAt(0).toUpperCase() + planType.slice(1)} Plan
                  </span>
                </div>

                {/* Pricing */}
                <div className="mb-12">
                  <div className="mb-4">
                    <span className="text-fg-60 text-sm">Start at</span>
                  </div>
                  <div className="flex items-baseline gap-2 mb-6">
                    {hasDiscount ? (
                      <>
                        <span className="text-3xl font-bold text-fg-60 line-through mr-2">
                          ${originalPrice}
                        </span>
                        <span className="text-4xl font-bold text-fg-40">
                          ${discountedPrice}
                        </span>
                      </>
                    ) : (
                      <span className="text-4xl font-bold text-fg-40">
                        ${originalPrice}
                      </span>
                    )}
                    <span className="text-fg-60 text-lg">/month</span>
                  </div>

                  {/* Discount Badge */}
                  {hasDiscount && (
                    <div>
                      <span className="bg-green-500/20 text-green-600 px-3 py-1 rounded-full text-xs font-medium border border-green-500/30">
                        Save ${discountAmount}/month with referral discount!
                      </span>
                    </div>
                  )}
                </div>

                {/* Usage Limits Table */}
                <div className="ml-1 mb-12">
                  <h4 className="text-fg-60 text-sm font-medium mb-8">
                    What's included
                  </h4>

                  {/* Table */}
                  <div className="space-y-4 mb-4">
                    {/* Table header */}
                    <div className="grid grid-cols-3 gap-4 pb-4 border-b border-bd-50">
                      <div className="text-fg-60 text-xs font-medium">
                        FEATURE
                      </div>
                      <div className="text-fg-60 text-xs font-medium">
                        DAILY
                      </div>
                      <div className="text-fg-60 text-xs font-medium">
                        MONTHLY
                      </div>
                    </div>

                    {/* Feature rows */}
                    <div className="space-y-3">
                      {features.map((feature, index) => (
                        <div
                          key={`limit-${index}`}
                          className="grid grid-cols-3 gap-4"
                        >
                          <div className="text-fg-60 text-sm">
                            {feature.label}
                          </div>
                          <div className="text-fg-40 text-sm font-normal">
                            {feature.daily}
                          </div>
                          <div className="text-fg-40 text-sm font-normal">
                            {feature.monthly}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="text-center py-12">
                <h3 className="text-xl font-semibold text-fg-50 mb-4">
                  No Plans Available
                </h3>
                <p className="text-fg-60 text-sm">
                  We're working on bringing you the best pricing plans. Please
                  check back later.
                </p>
              </div>
            )}

            {/* Bottom Section - Subscribe Button */}
            <div className="mb-4">
              {loading || loadingDiscount ? (
                <div className="space-y-3 animate-pulse">
                  <div className="h-10 w-full bg-bg-60 rounded-lg"></div>
                  <div className="h-4 w-full bg-bg-60 rounded"></div>
                </div>
              ) : (
                <>
                  <button
                    onClick={handleSubscribe}
                    disabled={isPageLoading || !plan}
                    className="w-full bg-ac-02 text-white dark:text-black py-3 px-6 rounded-lg text-sm font-medium hover:bg-ac-01 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed mb-4"
                  >
                    {isPageLoading ? "Processing..." : "Upgrade Plan"}
                  </button>

                  <p className="text-xs text-fg-60 text-center">
                    Your payment is secure and encrypted.
                  </p>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Right Section - Auth Showcase */}
        <AuthShowcase />

        {/* Full Page Loading Overlay */}
        {isPageLoading && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className="flex flex-col items-center gap-4">
              <div className="w-12 h-12 border-4 border-ac-02/30 border-t-ac-02 rounded-full animate-spin"></div>
              <p className="text-white text-sm font-medium">
                Processing your payment...
              </p>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default UpgradePlan;
